/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI, announcementsAPI, meetingsAPI } from '../lib/api';
import { TARGET_AUDIENCES, MEETING_TYPES, formatDate } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Megaphone,
  Calendar,
  Users,
  Building2,
  ChevronRight,
  MapPin,
  Clock,
  AlertTriangle,
  Shield,
  Vote,
  MessageSquare,
  LogIn,
  LayoutDashboard,
  ExternalLink,
} from 'lucide-react';

const HERO_BG = "/images/hero-bg.jpg";
const FOOTER_BG = "/images/footer-bg.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' } }),
};

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ users: 0, announcements: 0, meetings: 0, organs: 0 });
  const [publicAnns, setPublicAnns] = useState([]);
  const [publicMeetings, setPublicMeetings] = useState([]);
  const [personalAnns, setPersonalAnns] = useState([]);
  const [personalMeetings, setPersonalMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState('genel');

  useEffect(() => {
    publicAPI.getStats().then(r => setStats(r.data)).catch(() => {});
    publicAPI.getAnnouncements(6).then(r => setPublicAnns(r.data)).catch(() => {});
    publicAPI.getMeetings(6).then(r => setPublicMeetings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      announcementsAPI.getAll({ limit: 6 }).then(r => setPersonalAnns(r.data.announcements || [])).catch(() => {});
      meetingsAPI.getAll({ limit: 6 }).then(r => setPersonalMeetings(r.data.meetings || [])).catch(() => {});
    }
  }, [user]);

  const displayAnns = activeTab === 'ozel' && user ? personalAnns : publicAnns;
  const displayMeetings = activeTab === 'ozel' && user ? personalMeetings : publicMeetings;

  const priorityStyles = {
    acil: { border: 'border-l-red-500', bg: 'bg-red-50', text: 'text-red-700', label: 'Acil' },
    yuksek: { border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', label: 'Yuksek' },
    normal: { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', label: 'Normal' },
    dusuk: { border: 'border-l-slate-300', bg: 'bg-slate-50', text: 'text-slate-600', label: 'Dusuk' },
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* === HEADER === */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200" data-testid="homepage-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="homepage-logo">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-slate-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Kent Konseyi</p>
              <p className="text-xs text-slate-500 -mt-0.5">Dijital Katılım</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#duyurular" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Duyurular</a>
            <a href="#toplantilar" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Toplantılar</a>
            <Link to="/kurumsal" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Kurumsal</Link>
            <a href="#hakkinda" className="text-sm text-slate-600 hover:text-slate-900 transition-colors">Platform</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate('/panel')} className="h-9 gap-2 bg-blue-600 hover:bg-blue-700 rounded-full px-5" data-testid="hero-panel-btn">
                <LayoutDashboard className="w-4 h-4" /> Panele Git
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/giris')} className="h-9 text-sm text-slate-600" data-testid="header-login-btn">
                  <LogIn className="w-4 h-4 mr-1.5" /> Giriş Yap
                </Button>
                <Button onClick={() => navigate('/giris')} className="h-9 gap-2 bg-blue-600 hover:bg-blue-700 rounded-full px-5" data-testid="header-register-btn">
                  Kayıt Ol <ArrowRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* === HERO === */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-blue-900/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 w-full">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-3xl">
            <motion.div variants={fadeUp} custom={0}>
              <Badge className="bg-white/10 text-white/90 border-white/20 backdrop-blur-sm mb-6 text-xs tracking-wider uppercase px-3 py-1">
                Adana Kent Konseyi
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tighter leading-[1.1] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              Şehrinizin Geleceğinde{' '}
              <span className="text-blue-400">Söz Sahibi</span>{' '}
              Olun.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2}
              className="text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed mb-10">
              Dijital Katılım Platformu ile kararlara katılın, fikirlerinizi paylaşın ve şehrimizi birlikte yönetelim.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              {user ? (
                <Button size="lg" onClick={() => navigate('/panel')}
                  className="h-12 px-8 bg-blue-600 hover:bg-blue-700 rounded-full text-base gap-2 shadow-lg shadow-blue-600/30"
                  data-testid="hero-cta-button">
                  <LayoutDashboard className="w-5 h-5" /> Panele Git
                </Button>
              ) : (
                <>
                  <Button size="lg" onClick={() => navigate('/giris')}
                    className="h-12 px-8 bg-blue-600 hover:bg-blue-700 rounded-full text-base gap-2 shadow-lg shadow-blue-600/30"
                    data-testid="hero-cta-button">
                    Platforma Katıl <ArrowRight className="w-5 h-5" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate('/giris')}
                    className="h-12 px-8 rounded-full text-base border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
                    data-testid="hero-login-button">
                    Giriş Yap
                  </Button>
                </>
              )}
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Bento - overlapping hero bottom */}
        <div className="absolute bottom-0 left-0 right-0 translate-y-1/2 z-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {(() => {
                // stats.users backend'den bucket'li string geliyor (ornek: "10K+")
                // Diger degerler sayi, toLocaleString ile formatla
                const fmtNum = (v) => typeof v === 'string' ? v : (Number(v || 0)).toLocaleString('tr-TR');
                const items = [
                  { icon: Users, label: 'Vatandaş', value: fmtNum(stats.users), color: 'from-blue-600 to-blue-700' },
                  { icon: Megaphone, label: 'Duyuru', value: fmtNum(stats.announcements), color: 'from-emerald-600 to-emerald-700' },
                  { icon: Calendar, label: 'Toplantı', value: fmtNum(stats.meetings), color: 'from-amber-500 to-amber-600' },
                  { icon: Building2, label: 'Aktif Organ', value: fmtNum(stats.organs), color: 'from-purple-600 to-purple-700' },
                ];
                return items.map((stat, i) => (
                  <motion.div key={stat.label} variants={fadeUp} custom={i}
                    className="bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/50 p-5 md:p-6 text-center"
                    data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
                  </motion.div>
                ));
              })()}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Spacer for stats overlap */}
      <div className="h-24 md:h-28 bg-slate-50" />

      {/* === TAB SWITCHER (only if logged in) === */}
      {user && (
        <div className="bg-slate-50 pt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
            <div className="inline-flex bg-white rounded-full p-1 border border-slate-200 shadow-sm" data-testid="content-tab-switcher">
              <button onClick={() => setActiveTab('genel')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'genel' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="tab-genel">
                Genel
              </button>
              <button onClick={() => setActiveTab('ozel')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'ozel' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="tab-ozel">
                Size Özel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === ANNOUNCEMENTS === */}
      <section id="duyurular" className="bg-slate-50 py-20 md:py-28" data-testid="announcements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.div variants={fadeUp} className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs tracking-widest uppercase text-blue-600 font-semibold mb-2">Duyurular</p>
                <h2 className="text-3xl md:text-4xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {activeTab === 'ozel' ? 'Size Özel Duyurular' : 'Güncel Duyurular'}
                </h2>
              </div>
              {user && (
                <Link to="/panel/duyurular" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="view-all-announcements">
                  Tümünü Gör <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>

            {displayAnns.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayAnns.map((a, i) => {
                  const ps = priorityStyles[a.priority] || priorityStyles.normal;
                  return (
                    <motion.div key={a.id} variants={fadeUp} custom={i}
                      className={`bg-white rounded-2xl border border-slate-200 border-l-4 ${ps.border} p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
                      onClick={() => user ? navigate(`/panel/duyurular/${a.id}`) : navigate('/giris')}
                      data-testid={`public-ann-${a.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          {a.priority === 'acil' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                          <Badge className={`${ps.bg} ${ps.text} border-0 text-xs`}>{ps.label}</Badge>
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(a.created_at)}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{a.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-3 mb-4 leading-relaxed">{a.content}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1.5 flex-wrap">
                          {a.target_audience?.slice(0, 2).map(t => (
                            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {TARGET_AUDIENCES.find(ta => ta.value === t)?.label || t}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-slate-400">{a.created_by_name}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
                <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  {activeTab === 'ozel' ? 'Size özel duyuru bulunamadı.' : 'Henüz herkese açık duyuru yayınlanmadı.'}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* === MEETINGS === */}
      <section id="toplantilar" className="bg-white py-20 md:py-28" data-testid="meetings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.div variants={fadeUp} className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs tracking-widest uppercase text-emerald-600 font-semibold mb-2">Toplantılar</p>
                <h2 className="text-3xl md:text-4xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {activeTab === 'ozel' ? 'Size Ozel Toplantılar' : 'Yaklasan Toplantılar'}
                </h2>
              </div>
              {user && (
                <Link to="/panel/toplantilar" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="view-all-meetings">
                  Tümünü Gör <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>

            {displayMeetings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayMeetings.map((m, i) => {
                  const typeInfo = MEETING_TYPES[m.meeting_type] || { label: m.meeting_type, color: 'default' };
                  const typeGradients = {
                    genel_kurul: 'from-emerald-500 to-emerald-600',
                    yurutme_kurulu: 'from-orange-500 to-orange-600',
                    meclis: 'from-blue-500 to-blue-600',
                    calisma_grubu: 'from-purple-500 to-purple-600',
                    diger: 'from-slate-400 to-slate-500',
                  };
                  const gradient = typeGradients[m.meeting_type] || 'from-slate-400 to-slate-500';
                  return (
                    <motion.div key={m.id} variants={fadeUp} custom={i}
                      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                      onClick={() => user ? navigate(`/panel/toplantilar/${m.id}`) : navigate('/giris')}
                      data-testid={`public-mtg-${m.id}`}>
                      <div className={`h-1.5 bg-gradient-to-r ${gradient}`} />
                      <div className="p-6">
                        <div className="flex items-start justify-between gap-2 mb-4">
                          <Badge className="bg-slate-100 text-slate-700 border-0 text-xs">{typeInfo.label}</Badge>
                          <Badge variant="outline" className="text-xs border-slate-200 text-slate-400">
                            {m.status === 'planlanmis' ? 'Planlanmis' : 'Tamamlandi'}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-4 line-clamp-2 leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.title}</h3>
                        <div className="space-y-2.5">
                          <div className="flex items-center gap-3 text-sm">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Calendar className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="text-slate-900 font-medium text-sm">{formatDate(m.date)}</p>
                              <p className="text-xs text-slate-400">{m.time}</p>
                            </div>
                          </div>
                          {m.location && (
                            <div className="flex items-center gap-3 text-sm">
                              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-4 h-4 text-slate-500" />
                              </div>
                              <p className="text-sm text-slate-600 truncate">{m.location}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  {activeTab === 'ozel' ? 'Size özel toplantı bulunamadı.' : 'Henüz herkese açık toplantı planlanmadı.'}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* === FEATURES / WHY JOIN === */}
      <section id="hakkinda" className="bg-slate-50 py-20 md:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-xs tracking-widest uppercase text-blue-600 font-semibold mb-3">Neden Katılmalısınız?</p>
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Demokratik Katılımın Dijital Adresi
              </h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: MessageSquare,
                  title: 'Fikirlerinizi Paylaşın',
                  desc: 'Çalışma gruplarına katılın, tartışmalarda söz sahibi olun ve şehriniz için öneriler sunun.',
                  color: 'from-blue-600 to-blue-700',
                },
                {
                  icon: Vote,
                  title: 'Kararlara Katılın',
                  desc: 'Toplantılara katılın, oylamalarda oy kullanın ve kent politikalarını birlikte şekillendirin.',
                  color: 'from-emerald-600 to-emerald-700',
                },
                {
                  icon: Shield,
                  title: 'Şeffaf Yönetim',
                  desc: 'Tüm kararlar, toplantı tutanakları ve proje ilerlemeleri şeffaf bir şekilde paylaşılıyor.',
                  color: 'from-purple-600 to-purple-700',
                },
              ].map((feature, i) => (
                <motion.div key={feature.title} variants={fadeUp} custom={i}
                  className="bg-white rounded-2xl border border-slate-200 p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  data-testid={`feature-card-${i}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-medium text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{feature.title}</h3>
                  <p className="text-slate-500 leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* === CTA SECTION === */}
      {!user && (
        <section className="bg-slate-900 py-20 md:py-24" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <motion.h2 variants={fadeUp}
                className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
                Şehrinize Sahip Çıkın
              </motion.h2>
              <motion.p variants={fadeUp} custom={1} className="text-lg text-white/60 mb-10 max-w-lg mx-auto">
                Hemen kayıt olun ve Adana Kent Konseyi'nin dijital platformunda yerinizi alın.
              </motion.p>
              <motion.div variants={fadeUp} custom={2}>
                <Button size="lg" onClick={() => navigate('/giris')}
                  className="h-12 px-10 bg-blue-600 hover:bg-blue-700 rounded-full text-base gap-2 shadow-lg shadow-blue-600/30"
                  data-testid="cta-register-btn">
                  Hemen Kayıt Ol <ArrowRight className="w-5 h-5" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* === FOOTER === */}
      <footer className="relative overflow-hidden" data-testid="homepage-footer">
        <img src={FOOTER_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Adana Kent Konseyi</p>
                  <p className="text-xs text-white/50">Dijital Katilim Platformu</p>
                </div>
              </div>
              <p className="text-sm text-white/50 leading-relaxed">
                Kent Konseyi, yerel yönetim ile vatandaşlar arasında köprü görevi gören demokratik katılım platformudur.
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-white/80 mb-4 tracking-wide uppercase">Hızlı Erişim</p>
              <ul className="space-y-2.5">
                <li><a href="#duyurular" className="text-sm text-white/50 hover:text-white transition-colors">Duyurular</a></li>
                <li><a href="#toplantilar" className="text-sm text-white/50 hover:text-white transition-colors">Toplantılar</a></li>
                <li><Link to="/kurumsal" className="text-sm text-white/50 hover:text-white transition-colors">Kurumsal</Link></li>
                <li><a href="#hakkinda" className="text-sm text-white/50 hover:text-white transition-colors">Platform Hakkinda</a></li>
                <li><Link to="/giris" className="text-sm text-white/50 hover:text-white transition-colors">Giriş Yap / Kayıt Ol</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-white/80 mb-4 tracking-wide uppercase">İletişim</p>
              <ul className="space-y-2.5">
                <li className="text-sm text-white/50">Adana Büyükşehir Belediyesi</li>
                <li className="text-sm text-white/50">Kent Konseyi Başkanlığı</li>
                <li className="text-sm text-white/50">Adana, Türkiye</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30">2026 Adana Kent Konseyi. Tüm hakları saklıdır.</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-white/40">
              <Link to="/gizlilik-bildirimi" className="hover:text-white transition-colors" data-testid="footer-kvkk-link">KVKK Aydınlatma</Link>
              <span className="text-white/20">·</span>
              <Link to="/cerez-politikasi" className="hover:text-white transition-colors" data-testid="footer-cookie-link">Çerez Politikası</Link>
              <span className="text-white/20">·</span>
              <span>v2.0</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
